/**
 * PDF Export Service
 *
 * Coordinates PDF generation for different page types with professional layouts
 * suitable for printing and sharing. Integrates with jsPDF for document creation.
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { pageContextDetector } from './pageContextDetector.js';
import { pdfLayoutEngine } from './pdfLayoutEngine.js';

class PDFExportService {
  constructor() {
    this.isGenerating = false;
    this.supportedFormats = ['A4', 'Letter'];
    this.defaultOptions = {
      format: 'A4',
      orientation: 'portrait',
      unit: 'mm',
      putOnlyUsedFonts: true,
      floatPrecision: 16
    };
  }

  /**
   * Main export method - generates PDF based on current page context
   * @param {Object} context - Current application context
   * @returns {Promise<Object>} Export result with download info
   */
  async exportCurrentPage(context) {
    console.log('🏨 [DEBUG] pdfExportService.exportCurrentPage called:', {
      contextPathname: context.pathname,
      contextTrip: context.trip?.name,
      contextUser: context.user?.uid
    });

    if (this.isGenerating) {
      throw new Error('PDF export already in progress');
    }

    this.isGenerating = true;

    try {
      // Detect current page type and extract data
      console.log('🏨 [DEBUG] Calling pageContextDetector.detectContext');
      const pageContext = await pageContextDetector.detectContext(context);

      console.log('🏨 [DEBUG] Page context detected:', {
        pageType: pageContext.type,
        title: pageContext.title,
        hasData: !!pageContext.data,
        dataKeys: pageContext.data ? Object.keys(pageContext.data) : [],
        tripName: pageContext.tripName,
        pathname: pageContext.pathname
      });

      // Validate we have data to export
      if (!pageContext.data || this.isEmpty(pageContext.data)) {
        console.log('🏨 [DEBUG] No data found for export, throwing error');
        throw new Error(`No ${pageContext.type.toLowerCase()} data found to export`);
      }

      // Generate PDF content using layout engine
      console.log('🏨 [DEBUG] Calling pdfLayoutEngine.generateLayout');
      const pdfContent = await pdfLayoutEngine.generateLayout(pageContext);

      console.log('🏨 [DEBUG] PDF content generated:', {
        hasHeader: !!pdfContent.header,
        sectionsCount: pdfContent.sections?.length || 0,
        sectionTitles: pdfContent.sections?.map(s => s.title || s.type) || []
      });

      // Create PDF document
      const pdf = this.createPDFDocument();

      // Apply content to PDF
      this.applyContentToPDF(pdf, pdfContent);

      // Generate filename and download
      const filename = this.generateFilename(pageContext);

      return this.downloadPDF(pdf, filename, pageContext);

    } catch (error) {
      console.error('PDF Export failed:', error);
      throw error;
    } finally {
      this.isGenerating = false;
    }
  }

  /**
   * Get export preview information without generating PDF
   * @param {Object} context - Current application context
   * @returns {Promise<Object>} Preview information
   */
  async getExportPreview(context) {
    const pageContext = await pageContextDetector.detectContext(context);

    if (!pageContext.data || this.isEmpty(pageContext.data)) {
      return {
        available: false,
        reason: `No ${pageContext.type.toLowerCase()} data found`
      };
    }

    const stats = this.calculateContentStats(pageContext.data, pageContext.type);
    const filename = this.generateFilename(pageContext);

    return {
      available: true,
      pageType: pageContext.type,
      title: pageContext.title,
      filename: filename,
      estimatedPages: stats.estimatedPages,
      contentSummary: stats.summary,
      dataCount: stats.itemCount
    };
  }

  /**
   * Create new PDF document with default settings
   * @returns {jsPDF} PDF document instance
   */
  createPDFDocument() {
    return new jsPDF(this.defaultOptions);
  }

  /**
   * Apply generated content to PDF document
   * @param {jsPDF} pdf - PDF document instance
   * @param {Object} content - Generated PDF content structure
   */
  applyContentToPDF(pdf, content) {
    let currentY = 20; // Start position

    // Add header
    if (content.header) {
      currentY = this.addHeader(pdf, content.header, currentY);
      currentY += 10; // Space after header
    }

    // Add main content sections
    if (content.sections && content.sections.length > 0) {
      content.sections.forEach(section => {
        currentY = this.addSection(pdf, section, currentY);
        currentY += 5; // Space between sections

        // Add new page if needed
        if (currentY > 250) { // Near bottom of A4 page
          pdf.addPage();
          currentY = 20;
        }
      });
    }

    // NO FOOTER per specification - clean simple PDFs only
  }

  /**
   * Add header section to PDF
   * @param {jsPDF} pdf - PDF document instance
   * @param {Object} header - Header content
   * @param {number} startY - Starting Y position
   * @returns {number} New Y position
   */
  addHeader(pdf, header, startY) {
    // Title - FIXED LINE BREAK HANDLING WITH TEXT WRAPPING
    if (header.title) {
      console.log('PDF Header Generation Debug - Title Processing:', {
        originalTitle: header.title,
        titleLength: header.title.length,
        containsNewline: header.title.includes('\n'),
        charCodes: [...header.title].map(char => ({ char, code: char.charCodeAt(0) }))
      });

      pdf.setFontSize(20);
      pdf.setFont(undefined, 'bold');
      // Clean title text for PDF
      const cleanTitle = this.sanitizeTextForPDF(header.title);

      console.log('PDF Header Generation Debug - After Sanitization:', {
        cleanTitle,
        cleanTitleLength: cleanTitle.length,
        containsNewlineAfterSanitization: cleanTitle.includes('\n'),
        charCodesAfterSanitization: [...cleanTitle].map(char => ({ char, code: char.charCodeAt(0) }))
      });

      // Handle multi-line titles properly (split on \n)
      const titleLines = cleanTitle.split('\n');
      console.log('PDF Header Generation Debug - Title Lines:', {
        titleLines,
        numberOfLines: titleLines.length,
        linesDetail: titleLines.map((line, index) => ({ index, line, length: line.length, trimmed: line.trim() }))
      });

      // Wrap long lines to fit within page width (max width: 170mm for margins)
      const maxWidth = 170;
      const wrappedLines = [];
      titleLines.forEach((line) => {
        if (line.trim()) {
          // Use jsPDF's splitTextToSize to wrap text
          const wrapped = pdf.splitTextToSize(line.trim(), maxWidth);
          wrappedLines.push(...wrapped);
        }
      });

      console.log('PDF Header Generation Debug - After wrapping:', {
        wrappedLines,
        numberOfWrappedLines: wrappedLines.length
      });

      wrappedLines.forEach((line, index) => {
        console.log(`PDF Header Generation Debug - Adding line ${index}: "${line}" at Y position ${startY}`);
        pdf.text(line, 20, startY);
        startY += 10; // Move to next line
      });

      console.log('PDF Header Generation Debug - Final Y position after title:', startY);
    }

    // Subtitle
    if (header.subtitle) {
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'normal');
      // Clean subtitle text for PDF
      const cleanSubtitle = this.sanitizeTextForPDF(header.subtitle);
      pdf.text(cleanSubtitle, 20, startY);
      startY += 8;
    }

    // Trip dates
    if (header.dates) {
      pdf.setFontSize(12);
      pdf.setTextColor(100);
      const cleanDates = this.sanitizeTextForPDF(`Dates: ${header.dates}`);
      pdf.text(cleanDates, 20, startY);
      startY += 6;
    }

    // Export date
    pdf.setFontSize(10);
    pdf.setTextColor(150);
    pdf.text(this.sanitizeTextForPDF(`Exported: ${new Date().toLocaleDateString()}`), 20, startY);
    startY += 8;

    // Add line separator
    pdf.setDrawColor(0, 0, 0);
    pdf.line(20, startY, 190, startY);

    // Reset text color
    pdf.setTextColor(0);

    return startY + 5;
  }

  /**
   * Add content section to PDF
   * @param {jsPDF} pdf - PDF document instance
   * @param {Object} section - Section content
   * @param {number} startY - Starting Y position
   * @returns {number} New Y position
   */
  addSection(pdf, section, startY) {
    console.log('🏨 [DEBUG] pdfExportService.addSection called:', {
      sectionType: section.type,
      sectionTitle: section.title,
      hasItems: !!section.items,
      itemsCount: section.items?.length || 0,
      hasRows: !!section.rows,
      rowsCount: section.rows?.length || 0,
      hasContent: !!section.content,
      startY
    });

    // Section title
    if (section.title) {
      // "Previously Deleted AI Resources" uses category heading style (11pt) to match category headings
      const isDeletedResourcesSection = section.title.includes('Previously Deleted AI Resources');

      if (isDeletedResourcesSection) {
        pdf.setFontSize(11);
        pdf.setFont(undefined, 'bold');
        pdf.text(this.sanitizeTextForPDF(section.title), 25, startY); // Match category heading left margin
        startY += 6; // Match category heading spacing
        console.log('🏨 [DEBUG] Added section title:', section.title, '(category heading style)');
      } else {
        pdf.setFontSize(16);
        pdf.setFont(undefined, 'bold');
        pdf.text(this.sanitizeTextForPDF(section.title), 20, startY);
        startY += 8;
        console.log('🏨 [DEBUG] Added section title:', section.title, '(section title style)');
      }
    }

    // Handle different section types
    console.log('🏨 [DEBUG] Processing section type:', section.type);
    switch (section.type) {
      case 'table':
        console.log('🏨 [DEBUG] Calling addTableSection');
        return this.addTableSection(pdf, section, startY);
      case 'timeline':
        console.log('🏨 [DEBUG] Calling addTimelineSection');
        return this.addTimelineSection(pdf, section, startY);
      case 'itinerary':
        console.log('🏨 [DEBUG] Calling addItinerarySection');
        return this.addItinerarySection(pdf, section, startY);
      case 'list':
        console.log('🏨 [DEBUG] Calling addListSection');
        return this.addListSection(pdf, section, startY);
      case 'text':
        console.log('🏨 [DEBUG] Calling addTextSection');
        return this.addTextSection(pdf, section, startY);
      default:
        console.warn(`Unknown section type: ${section.type}`);
        return startY;
    }
  }

  /**
   * Add table section using autoTable
   * @param {jsPDF} pdf - PDF document instance
   * @param {Object} section - Table section content
   * @param {number} startY - Starting Y position
   * @returns {number} New Y position
   */
  addTableSection(pdf, section, startY) {
    if (!section.columns || !section.rows) {
      return startY;
    }

    autoTable(pdf, {
      head: [section.columns],
      body: section.rows,
      startY: startY,
      styles: {
        fontSize: 10,
        cellPadding: 3
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      margin: { left: 20, right: 20 }
    });

    return pdf.lastAutoTable.finalY + 5;
  }

  /**
   * Add timeline section for days/itinerary
   * @param {jsPDF} pdf - PDF document instance
   * @param {Object} section - Timeline section content
   * @param {number} startY - Starting Y position
   * @returns {number} New Y position
   */
  addTimelineSection(pdf, section, startY) {
    if (!section.items || !Array.isArray(section.items)) {
      return startY;
    }

    pdf.setFontSize(10);

    section.items.forEach(item => {
      // Date/time
      if (item.date) {
        pdf.setFont(undefined, 'bold');
        pdf.text(this.sanitizeTextForPDF(item.date), 20, startY);
        startY += 5;
      }

      // Title
      if (item.title) {
        pdf.setFont(undefined, 'bold');
        pdf.text(this.sanitizeTextForPDF(item.title), 25, startY);
        startY += 5;
      }

      // Description
      if (item.description) {
        pdf.setFont(undefined, 'normal');
        const lines = pdf.splitTextToSize(item.description, 160);
        pdf.text(lines, 25, startY);
        startY += lines.length * 4;
      }

      startY += 3; // Space between items
    });

    return startY;
  }

  /**
   * Add itinerary section for Daily Itinerary PDFs
   * @param {jsPDF} pdf - PDF document instance
   * @param {Object} section - Itinerary section content
   * @param {number} startY - Starting Y position
   * @returns {number} New Y position
   */
  addItinerarySection(pdf, section, startY) {
    if (!section.items || !Array.isArray(section.items)) {
      return startY;
    }

    pdf.setFontSize(10);

    section.items.forEach(item => {
      // Debug logging to see what data we have
      if (item._debug) {
        console.log('PDF Day Debug:', item._debug);
      }

      // Replicate the exact page format:
      // Day number and date on first line (bold)
      if (item.dayNumber && item.dayDate) {
        pdf.setFont(undefined, 'bold');
        pdf.setFontSize(12);
        const cleanDayHeader = this.sanitizeTextForPDF(`${item.dayNumber} - ${item.dayDate}`);
        pdf.text(cleanDayHeader, 20, startY);
        startY += 8;
        pdf.setFontSize(10); // Reset to normal size
      }

      // Day content (exactly what ContentBlockRenderer would show)
      if (item.hasContent && item.dayContent) {
        pdf.setFont(undefined, 'normal');

        // Simple content display - just show the content as-is
        // ContentBlockRenderer would handle formatting, but for PDF we keep it simple
        const contentLines = this.splitContentIntoLines(item.dayContent);

        contentLines.forEach(line => {
          if (line.trim()) {
            const cleanLine = this.sanitizeTextForPDF(line.trim());
            pdf.text(cleanLine, 25, startY);
            startY += 5;
          }
        });
      } else if (!item.hasContent) {
        // Show "No content" for empty days (like the page would)
        pdf.setFont(undefined, 'italic');
        pdf.text(this.sanitizeTextForPDF('No content'), 25, startY);
        startY += 5;
      }

      startY += 6; // Space between days

      // Add new page if needed
      if (startY > 250) { // Near bottom of A4 page
        pdf.addPage();
        startY = 20;
      }
    });

    return startY;
  }

  /**
   * Split content into lines for PDF display
   * @param {string} content - Content to split
   * @returns {Array} Array of lines
   */
  splitContentIntoLines(content) {
    if (!content) return [];

    // Handle both newlines and very long lines
    const lines = content.split('\n');
    const result = [];

    lines.forEach(line => {
      if (line.length > 80) {
        // Split long lines at word boundaries
        const words = line.split(' ');
        let currentLine = '';

        words.forEach(word => {
          if ((currentLine + ' ' + word).length > 80 && currentLine) {
            result.push(currentLine);
            currentLine = word;
          } else {
            currentLine = currentLine ? currentLine + ' ' + word : word;
          }
        });

        if (currentLine) {
          result.push(currentLine);
        }
      } else {
        result.push(line);
      }
    });

    return result;
  }

  /**
   * Add list section
   * @param {jsPDF} pdf - PDF document instance
   * @param {Object} section - List section content
   * @param {number} startY - Starting Y position
   * @returns {number} New Y position
   */
  addListSection(pdf, section, startY) {
    console.log('🏨 [DEBUG] addListSection called:', {
      hasItems: !!section.items,
      isArray: Array.isArray(section.items),
      itemsCount: section.items?.length || 0,
      items: section.items,
      startY
    });

    if (!section.items || !Array.isArray(section.items)) {
      console.log('🏨 [DEBUG] addListSection returning early - no items or not array');
      return startY;
    }

    // Page width: 210mm (A4), margins: 20mm left + 20mm right = 170mm = ~160 points
    const maxWidth = 160;
    const leftMargin = 25;

    // ALWAYS reset font to normal at start of section to prevent bold state from persisting
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'normal');

    section.items.forEach((item, index) => {
      // Handle empty items (spacing between categories)
      if (!item || item.trim() === '') {
        startY += 3;
        return;
      }

      // Check if this is a category heading (marked with HEADING: prefix)
      if (item.startsWith('HEADING:')) {
        const headingText = item.substring(8); // Remove "HEADING:" prefix
        const cleanHeading = this.sanitizeTextForPDF(headingText);

        // Category headings: bold, slightly larger font (11pt), no bullet
        pdf.setFontSize(11);
        pdf.setFont(undefined, 'bold');
        pdf.text(cleanHeading, leftMargin, startY);
        console.log('🏨 [DEBUG] Added category heading:', cleanHeading, 'at Y:', startY);
        startY += 6; // Slightly more spacing after heading

        // Reset to normal formatting for items
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
      } else {
        // Regular list item: add hyphen bullet, word wrap with right margin
        const cleanItem = this.sanitizeTextForPDF(item);
        const bulletPoint = `- ${cleanItem}`;

        // Use splitTextToSize for word wrap with proper right margin
        const lines = pdf.splitTextToSize(bulletPoint, maxWidth);

        // Add each wrapped line
        lines.forEach((line, lineIndex) => {
          pdf.text(line, leftMargin, startY);
          startY += 5; // Standard line spacing
        });

        console.log('🏨 [DEBUG] Added bullet point (', lines.length, 'lines):', cleanItem.substring(0, 50), '... at Y:', startY);
      }
    });

    console.log('🏨 [DEBUG] addListSection finished, returning Y:', startY);
    return startY;
  }

  /**
   * Add text section
   * @param {jsPDF} pdf - PDF document instance
   * @param {Object} section - Text section content
   * @param {number} startY - Starting Y position
   * @returns {number} New Y position
   */
  addTextSection(pdf, section, startY) {
    if (!section.content) {
      return startY;
    }

    pdf.setFontSize(10);
    pdf.setFont(undefined, 'normal');

    // Clean and encode text content for PDF
    const cleanContent = this.sanitizeTextForPDF(section.content);
    const lines = pdf.splitTextToSize(cleanContent, 170);
    pdf.text(lines, 20, startY);

    return startY + (lines.length * 4);
  }

  // FOOTER FUNCTIONALITY REMOVED per specification - clean simple PDFs only

  /**
   * Generate appropriate filename for the export
   * @param {Object} pageContext - Page context information
   * @returns {string} Generated filename
   */
  generateFilename(pageContext) {
    // Ensure we're using current date in YYYY-MM-DD format consistently
    const today = new Date();
    const date = today.getFullYear() + '-' +
                 String(today.getMonth() + 1).padStart(2, '0') + '-' +
                 String(today.getDate()).padStart(2, '0');

    const tripName = pageContext.tripName ?
      pageContext.tripName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase() :
      'trip';

    const pageType = pageContext.type.toLowerCase().replace(/\s+/g, '-');

    return `${tripName}-${pageType}-${date}.pdf`;
  }

  /**
   * Download generated PDF
   * @param {jsPDF} pdf - PDF document instance
   * @param {string} filename - Filename for download
   * @param {Object} pageContext - Page context information
   * @returns {Object} Download result
   */
  downloadPDF(pdf, filename, pageContext) {
    try {
      pdf.save(filename);

      return {
        success: true,
        filename: filename,
        pageType: pageContext.type,
        downloadedAt: new Date().toISOString()
      };
    } catch (error) {
      throw new Error(`Failed to download PDF: ${error.message}`);
    }
  }

  /**
   * Calculate content statistics for preview
   * @param {*} data - Data to analyze
   * @param {string} type - Page type
   * @returns {Object} Content statistics
   */
  calculateContentStats(data, type) {
    let itemCount = 0;
    let summary = '';
    let estimatedPages = 1;

    switch (type) {
      case 'Days List':
        itemCount = Array.isArray(data) ? data.length : 0;
        summary = `${itemCount} days in itinerary`;
        estimatedPages = Math.max(1, Math.ceil(itemCount / 3));
        break;
      case 'Places List':
        itemCount = Array.isArray(data) ? data.length : 0;
        summary = `${itemCount} places and locations`;
        estimatedPages = Math.max(1, Math.ceil(itemCount / 5));
        break;
      case 'Travel List':
        itemCount = Array.isArray(data) ? data.length : 0;
        summary = `${itemCount} travel arrangements`;
        estimatedPages = Math.max(1, Math.ceil(itemCount / 4));
        break;
      case 'Accommodation List':
        itemCount = Array.isArray(data) ? data.length : 0;
        summary = `${itemCount} accommodation bookings`;
        estimatedPages = Math.max(1, Math.ceil(itemCount / 3));
        break;
      case 'Documents List':
        itemCount = Array.isArray(data) ? data.length : 0;
        summary = `${itemCount} documents and files`;
        estimatedPages = Math.max(1, Math.ceil(itemCount / 8));
        break;
      default:
        summary = 'Page content';
        estimatedPages = 1;
    }

    return {
      itemCount,
      summary,
      estimatedPages
    };
  }

  /**
   * Sanitize text content for PDF export to prevent encoding issues
   * @param {string} text - Text content to sanitize
   * @returns {string} Sanitized text
   */
  sanitizeTextForPDF(text) {
    if (!text || typeof text !== 'string') {
      return '';
    }

    console.log('PDF Sanitization Debug - Input:', {
      text,
      length: text.length,
      containsNewline: text.includes('\n'),
      charCodes: [...text].map(char => ({ char, code: char.charCodeAt(0) }))
    });

    let cleanText = text;

    // AGGRESSIVE Unicode cleaning - remove ALL non-ASCII characters that could cause encoding issues
    // Replace common Unicode characters with ASCII equivalents
    cleanText = cleanText.replace(/[""]/g, '"'); // Smart quotes
    cleanText = cleanText.replace(/['']/g, "'"); // Smart apostrophes
    cleanText = cleanText.replace(/[–—]/g, '-'); // Em/en dashes
    cleanText = cleanText.replace(/[…]/g, '...'); // Ellipsis

    // Remove ALL emoji and symbol characters (Unicode ranges that cause jsPDF issues)
    cleanText = cleanText.replace(/[\u{1F600}-\u{1F64F}]/gu, ''); // Emoticons
    cleanText = cleanText.replace(/[\u{1F300}-\u{1F5FF}]/gu, ''); // Misc symbols
    cleanText = cleanText.replace(/[\u{1F680}-\u{1F6FF}]/gu, ''); // Transport symbols
    cleanText = cleanText.replace(/[\u{2600}-\u{26FF}]/gu, ''); // Misc symbols
    cleanText = cleanText.replace(/[\u{2700}-\u{27BF}]/gu, ''); // Dingbats
    cleanText = cleanText.replace(/[\u{1F900}-\u{1F9FF}]/gu, ''); // Supplemental symbols

    // Remove specific problematic characters
    cleanText = cleanText.replace(/[•]/g, '-'); // Bullet points
    cleanText = cleanText.replace(/[★☆]/g, '*'); // Stars
    cleanText = cleanText.replace(/[♠♥♦♣]/g, ''); // Card suits
    cleanText = cleanText.replace(/[℃℉]/g, 'deg'); // Temperature symbols

    // Replace accented characters with basic equivalents
    cleanText = cleanText.replace(/[àáâãäå]/g, 'a');
    cleanText = cleanText.replace(/[èéêë]/g, 'e');
    cleanText = cleanText.replace(/[ìíîï]/g, 'i');
    cleanText = cleanText.replace(/[òóôõö]/g, 'o');
    cleanText = cleanText.replace(/[ùúûü]/g, 'u');
    cleanText = cleanText.replace(/[ñ]/g, 'n');
    cleanText = cleanText.replace(/[ç]/g, 'c');
    cleanText = cleanText.replace(/[ß]/g, 'ss');
    cleanText = cleanText.replace(/[Ø]/g, 'O');

    // Replace HTML-like characters
    cleanText = cleanText.replace(/[<>]/g, '');
    cleanText = cleanText.replace(/[&]/g, 'and');

    // Preserve arrow character for routes (replace with ASCII equivalent before removing non-ASCII)
    cleanText = cleanText.replace(/→/g, '->');

    // Remove any remaining non-ASCII characters that could cause problems
    cleanText = cleanText.replace(/[^\x20-\x7E\n\r\t]/g, '');

    // Clean up whitespace - PRESERVE NEWLINES for PDF titles
    cleanText = cleanText.replace(/\r\n/g, '\n');
    cleanText = cleanText.replace(/\r/g, '\n');

    // Replace multiple spaces with single space, but PRESERVE newlines
    cleanText = cleanText.replace(/[ \t]+/g, ' '); // Only collapse spaces and tabs, not newlines

    // Remove leading/trailing whitespace from each line, but preserve the lines
    cleanText = cleanText.split('\n').map(line => line.trim()).join('\n');

    console.log('PDF Sanitization Debug - Output:', {
      cleanText,
      length: cleanText.length,
      containsNewlineAfter: cleanText.includes('\n'),
      charCodesAfter: [...cleanText].map(char => ({ char, code: char.charCodeAt(0) })),
      splitLines: cleanText.split('\n')
    });

    return cleanText;
  }

  /**
   * Check if data is empty
   * @param {*} data - Data to check
   * @returns {boolean} Whether data is empty
   */
  isEmpty(data) {
    if (!data) return true;
    if (Array.isArray(data)) return data.length === 0;
    if (typeof data === 'object') return Object.keys(data).length === 0;
    return false;
  }
}

// Export singleton instance
export const pdfExportService = new PDFExportService();
export default pdfExportService;